import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  company_id: string | null;
  phone: string | null;
  position: string | null;
  avatar_url: string | null;
  preferred_module: string | null;
}

type UserRole = "super_admin" | "admin" | "user" | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, metadata?: { first_name?: string; last_name?: string }) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  userRole: UserRole;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const initializedRef = React.useRef(false);
  const lastUserIdRef = React.useRef<string | null>(null);

  const resetAuthState = () => {
    setSession(null);
    setUser(null);
    setProfile(null);
    setIsAdmin(false);
    setUserRole(null);
    lastUserIdRef.current = null;
  };

  useEffect(() => {
    // 1. Initial session load - this is the primary path
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session?.access_token) {
        resetAuthState();
        setLoading(false);
        initializedRef.current = true;
        return;
      }

      const { data: userData, error: userError } = await supabase.auth.getUser();

      if (userError || !userData.user) {
        await supabase.auth.signOut({ scope: "local" });
        resetAuthState();
        setLoading(false);
        initializedRef.current = true;
        return;
      }

      setSession(session);
      setUser(userData.user);
      lastUserIdRef.current = userData.user.id;

      Promise.all([
        checkAdminRole(userData.user.id),
        fetchProfile(userData.user.id),
      ]).finally(() => {
        setLoading(false);
        initializedRef.current = true;
      });
    })();

    // 2. Listen for subsequent auth changes (sign in/out/token refresh)
    // IMPORTANT: Do NOT toggle `loading` on TOKEN_REFRESHED / USER_UPDATED or on
    // SIGNED_IN events that re-fire for the same user (e.g. when a Chrome tab
    // regains focus). Otherwise layouts that gate rendering on `loading` will
    // unmount their children and wipe form state. Only refetch profile/role
    // when the user actually changes or signs out.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Always keep session/user in sync silently
        setSession(session);
        setUser(session?.user ?? null);

        if (!initializedRef.current) return; // Skip during initial load

        // Token refresh / user metadata update: nothing else to do
        if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
          return;
        }

        if (event === "SIGNED_OUT" || !session?.user) {
          resetAuthState();
          setLoading(false);
          return;
        }

        // SIGNED_IN (or INITIAL_SESSION re-fire): only refetch if user changed
        if (lastUserIdRef.current === session.user.id) return;
        lastUserIdRef.current = session.user.id;

        setLoading(true);
        setTimeout(() => {
          Promise.all([
            checkAdminRole(session.user.id),
            fetchProfile(session.user.id),
          ]).finally(() => setLoading(false));
        }, 0);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, company_id, phone, position, avatar_url, preferred_module")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
        setProfile(null);
        return;
      }

      // Consistency check: if profile has no company_id, try to auto-link
      // it to the matching approved company by email (covers users whose
      // company was created/approved separately).
      if (data && !data.company_id) {
        try {
          const { data: linkedId, error: linkErr } = await supabase.rpc(
            "ensure_profile_company_link" as any
          );
          if (!linkErr && linkedId) {
            setProfile({ ...data, company_id: linkedId as string });
            return;
          }
        } catch (e) {
          console.warn("ensure_profile_company_link failed:", e);
        }
      }

      setProfile(data);
      if (data?.company_id) void supabase.functions.invoke("notify-new-customer", { body: { companyId: data.company_id } });
    } catch (error) {
      console.error("Error fetching profile:", error);
      setProfile(null);
    }
  };

  const checkAdminRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .single();

      if (error || !data) {
        console.error("Error checking admin role:", error);
        setIsAdmin(false);
        setUserRole(null);
        return;
      }

      const role = data.role as "super_admin" | "admin" | "user";
      setUserRole(role);
      setIsAdmin(role === "super_admin" || role === "admin");
    } catch (error) {
      console.error("Error checking admin role:", error);
      setIsAdmin(false);
      setUserRole(null);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error && data.user) {
      supabase.from("profiles").update({ last_login_at: new Date().toISOString() }).eq("id", data.user.id).then(() => {});
    }
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, metadata?: { first_name?: string; last_name?: string }) => {
    const redirectUrl = `${window.location.origin}/`;
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { emailRedirectTo: redirectUrl, data: metadata },
    });
    if (!error && data?.user && (!data.user.identities || data.user.identities.length === 0)) {
      return { error: new Error("User already registered") };
    }
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    resetAuthState();
  };

  return (
    <AuthContext.Provider value={{ user, session, profile, loading, signIn, signUp, signOut, isAdmin, userRole }}>
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================
// View-As (admin impersonation view) — overrides what useAuth() returns
// while still keeping the real authenticated session for mutations.
// ============================================================

interface ViewAsContextType {
  realUser: User | null;
  realIsAdmin: boolean;
}

const ViewAsContext = createContext<ViewAsContextType | undefined>(undefined);

interface ViewAsProviderProps {
  targetUser: User;
  targetProfile: Profile | null;
  targetUserRole: UserRole;
  children: ReactNode;
}

export function ViewAsProvider({ targetUser, targetProfile, targetUserRole, children }: ViewAsProviderProps) {
  const parent = useContext(AuthContext);
  if (!parent) throw new Error("ViewAsProvider must be inside AuthProvider");

  const overridden: AuthContextType = {
    ...parent,
    user: targetUser,
    profile: targetProfile,
    userRole: targetUserRole,
    isAdmin: targetUserRole === "super_admin" || targetUserRole === "admin",
  };

  return (
    <ViewAsContext.Provider value={{ realUser: parent.user, realIsAdmin: parent.isAdmin }}>
      <AuthContext.Provider value={overridden}>{children}</AuthContext.Provider>
    </ViewAsContext.Provider>
  );
}

export function useViewAs() {
  return useContext(ViewAsContext);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
